import type { Scene } from "@babylonjs/core/scene";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { SPECIES_IDS, SPECIES, bmax } from "../sim/species";
import { createPlantMaterial } from "./plantMaterial";
import { buildSkeleton } from "./skeletons";
import type { Terrain } from "./terrain";
import type { SimClient } from "../worker/client";

// Side-effect: adds thinInstance* methods to Mesh in the tree-shaken ES6 build.
import "@babylonjs/core/Meshes/thinInstanceMesh";

/**
 * Renders the sim as instanced, continuously-growing plants. Each species is one
 * thin-instance skeleton mesh; per-plant biomass (interpolated between worker
 * snapshots) drives a growth front (`instGrowth`) that the shader sweeps up the
 * plant to unfurl it base-to-tip. The instance matrix carries only position +
 * yaw (unit scale) — growth is entirely a shader parameter, no geometry rebuilds.
 */
const SNAP_MS = 100;
const CAPACITY = 8192;
const UNIT = new Vector3(1, 1, 1);
// Growth-front smoothing rate (per second). Each plant's displayed biomass eases
// toward the sim's latest value continuously (decoupled from the 10 Hz snapshot
// cadence), and every plant emerges from ~0 — so growth is smooth and plants
// sprout in rather than popping to seedling size.
const GROW_K = 2.2;
const ROWAN = SPECIES_IDS.indexOf("rowan");
// Lay a disc (built in the XY plane) flat on the ground.
const FLAT = Quaternion.RotationYawPitchRoll(0, Math.PI / 2, 0);

interface SpeciesRender {
  mesh: Mesh;
  matrices: Float32Array;
  growth: Float32Array;
  stress: Float32Array;
  phase: Float32Array;
  leanX: Float32Array;
  leanZ: Float32Array;
}

interface Dying {
  sp: number;
  x: number;
  z: number;
  y: number;
  g: number;
  phase: number;
  t: number;
}

const DEATH_MS = 6000;

export class GardenRenderer {
  private readonly species: SpeciesRender[] = [];
  private readonly refB: number[] = [];
  private readonly material: ShaderMaterial;
  private readonly engine: { getDeltaTime(): number };
  private lastCurTime = -1;
  private dying: Dying[] = [];
  // Per sim-slot smoothed biomass + a "seen alive" flag to detect (re)births.
  private readonly smoothB = new Float32Array(CAPACITY);
  private readonly seen = new Uint8Array(CAPACITY);
  // Soft contact-shadow discs under trees (one instanced mesh, filled per frame).
  private readonly treeShadow: Mesh;
  private readonly shadowMat = new Float32Array(16 * CAPACITY);

  constructor(
    scene: Scene,
    private readonly terrain: Terrain,
    private readonly sim: SimClient,
  ) {
    this.engine = scene.getEngine();
    this.material = createPlantMaterial(scene);
    for (const id of SPECIES_IDS) {
      const mesh = buildSkeleton(scene, id);
      mesh.material = this.material;
      mesh.isPickable = false;
      mesh.alwaysSelectAsActiveMesh = true; // thin instances span the world
      const sr: SpeciesRender = {
        mesh,
        matrices: new Float32Array(16 * CAPACITY),
        growth: new Float32Array(CAPACITY),
        stress: new Float32Array(CAPACITY),
        phase: new Float32Array(CAPACITY),
        leanX: new Float32Array(CAPACITY),
        leanZ: new Float32Array(CAPACITY),
      };
      // Allocate every instance buffer ONCE, as updatable (staticBuffer=false).
      // Per frame we mutate these arrays in place and re-upload the used range via
      // thinInstancePartialBufferUpdate. Calling thinInstanceSetBuffer every frame
      // instead LEAKS a GPU buffer per custom attribute per frame — that path
      // creates a new VertexBuffer and overwrites the reference without disposing
      // the old one, exhausting VRAM until the WebGL context is lost (the freeze).
      mesh.thinInstanceSetBuffer("matrix", sr.matrices, 16, false);
      mesh.thinInstanceSetBuffer("instGrowth", sr.growth, 1, false);
      mesh.thinInstanceSetBuffer("instStress", sr.stress, 1, false);
      mesh.thinInstanceSetBuffer("instPhase", sr.phase, 1, false);
      mesh.thinInstanceSetBuffer("instLeanX", sr.leanX, 1, false);
      mesh.thinInstanceSetBuffer("instLeanZ", sr.leanZ, 1, false);
      mesh.thinInstanceCount = 0;
      this.species.push(sr);
      this.refB.push(bmax(SPECIES[id]));
    }

    // Contact shadows: a dark, flat, translucent disc under each tree so it reads
    // as rooted rather than floating. One instanced mesh, matrices filled per frame.
    this.treeShadow = CreateDisc("treeShadow", { radius: 1, tessellation: 18 }, scene);
    const shMat = new StandardMaterial("treeShadowMat", scene);
    shMat.disableLighting = true;
    shMat.emissiveColor = new Color3(0, 0, 0);
    shMat.alpha = 0.26;
    shMat.backFaceCulling = false;
    this.treeShadow.material = shMat;
    this.treeShadow.isPickable = false;
    this.treeShadow.alwaysSelectAsActiveMesh = true;
    this.treeShadow.thinInstanceSetBuffer("matrix", this.shadowMat, 16, false);
    this.treeShadow.thinInstanceCount = 0;

    scene.onBeforeRenderObservable.add(() => this.update());
  }

  private update(): void {
    this.material.setFloat("time", performance.now() / 1000);
    const cur = this.sim.cur;
    if (!cur) return;
    const prev = this.sim.prev;
    const alpha = Math.min(1, (performance.now() - this.sim.curArrivedAt) / SNAP_MS);
    const dtMs = this.engine.getDeltaTime();
    const growAlpha = 1 - Math.exp(-GROW_K * Math.min(dtMs, 100) / 1000);

    if (cur.time !== this.lastCurTime && prev) {
      this.lastCurTime = cur.time;
      for (let i = 0; i < prev.count; i++) {
        if (prev.alive[i] === 1 && (i >= cur.count || cur.alive[i] === 0)) {
          const sp = prev.species[i]!;
          const spParams = SPECIES[SPECIES_IDS[sp]!];
          const x = prev.x[i]!;
          const z = prev.z[i]!;
          // Wilt from the size actually on screen (smoothed), not the raw sim value.
          const startB = this.smoothB[i]! > 0 ? this.smoothB[i]! : prev.biomass[i]!;
          const g = clamp(Math.pow(startB / this.refB[sp]!, spParams.heightExp), 0.02, 1);
          this.dying.push({ sp, x, z, y: this.terrain.heightAt(x, z), g, phase: hash(i) * 6.283, t: 0 });
          this.seen[i] = 0; // slot may be reused by a new plant → treat as a fresh birth
        }
      }
    }

    const counts = [0, 0, 0, 0];
    let shadowN = 0;
    const pos = new Vector3();
    const rot = new Quaternion();
    const scl = new Vector3();
    const m = new Matrix();

    for (let i = 0; i < cur.count; i++) {
      if (cur.alive[i] === 0) continue;
      const sp = cur.species[i]!;
      const spParams = SPECIES[SPECIES_IDS[sp]!];
      // Continuous growth: ease the displayed biomass toward the sim's value, and
      // start a fresh plant from ~0 so it visibly sprouts instead of popping in.
      if (this.seen[i] === 0) {
        this.smoothB[i] = 0;
        this.seen[i] = 1;
      }
      this.smoothB[i]! += (cur.biomass[i]! - this.smoothB[i]!) * growAlpha;
      const B = this.smoothB[i]!;
      let stress = cur.stress[i]!;
      let leanX = cur.leanX[i]!;
      let leanZ = cur.leanZ[i]!;
      if (prev && i < prev.count && prev.alive[i] === 1 && prev.species[i] === sp) {
        stress = prev.stress[i]! + (stress - prev.stress[i]!) * alpha;
        leanX = prev.leanX[i]! + (leanX - prev.leanX[i]!) * alpha;
        leanZ = prev.leanZ[i]! + (leanZ - prev.leanZ[i]!) * alpha;
      }
      const g = clamp(Math.pow(B / this.refB[sp]!, spParams.heightExp), 0.02, 1);

      const x = cur.x[i]!;
      const z = cur.z[i]!;
      pos.set(x, this.terrain.heightAt(x, z), z);
      Quaternion.RotationYawPitchRollToRef(hash(i) * 6.283, 0, 0, rot);
      Matrix.ComposeToRef(UNIT, rot, pos, m);

      const r = this.species[sp]!;
      const n = counts[sp]!;
      if (n < CAPACITY) {
        m.copyToArray(r.matrices, n * 16);
        r.growth[n] = g;
        r.stress[n] = stress;
        r.phase[n] = hash(i) * 6.283;
        r.leanX[n] = leanX;
        r.leanZ[n] = leanZ;
        counts[sp]! = n + 1;
      }

      // Contact shadow under trees, growing with the crown.
      if (sp === ROWAN && shadowN < CAPACITY) {
        const crownR = 0.6 + g * 2.4;
        scl.set(crownR, crownR, crownR);
        pos.set(x, pos.y + 0.05, z);
        Matrix.ComposeToRef(scl, FLAT, pos, m);
        m.copyToArray(this.shadowMat, shadowN * 16);
        shadowN++;
      }
    }

    this.treeShadow.thinInstanceCount = shadowN;
    if (shadowN > 0) this.treeShadow.thinInstancePartialBufferUpdate("matrix", this.shadowMat.subarray(0, shadowN * 16), 0);

    // Wilting plants: keep their grown structure but tilt over and sink to litter.
    for (let d = this.dying.length - 1; d >= 0; d--) {
      const dp = this.dying[d]!;
      dp.t += dtMs;
      const w = dp.t / DEATH_MS;
      if (w >= 1) {
        this.dying.splice(d, 1);
        continue;
      }
      const fall = smooth(0, 0.5, w) * 1.45;
      const shrink = 1 - smooth(0.5, 1, w) * 0.9;
      const sink = smooth(0.65, 1, w) * 0.3;
      pos.set(dp.x, dp.y - sink, dp.z);
      scl.set(shrink, shrink, shrink);
      Quaternion.RotationYawPitchRollToRef(dp.phase, fall, 0, rot);
      Matrix.ComposeToRef(scl, rot, pos, m);
      const r = this.species[dp.sp]!;
      const n = counts[dp.sp]!;
      if (n < CAPACITY) {
        m.copyToArray(r.matrices, n * 16);
        r.growth[n] = dp.g;
        r.stress[n] = 1; // browned litter
        r.phase[n] = dp.phase;
        r.leanX[n] = 0;
        r.leanZ[n] = 0;
        counts[dp.sp]! = n + 1;
      }
    }

    for (let s = 0; s < this.species.length; s++) {
      const r = this.species[s]!;
      const n = counts[s]!;
      r.mesh.thinInstanceCount = n;
      if (n === 0) continue;
      // Re-upload only the used range into the buffers allocated in the ctor —
      // no per-frame GPU allocation (see the note there).
      r.mesh.thinInstancePartialBufferUpdate("matrix", r.matrices.subarray(0, n * 16), 0);
      r.mesh.thinInstancePartialBufferUpdate("instGrowth", r.growth.subarray(0, n), 0);
      r.mesh.thinInstancePartialBufferUpdate("instStress", r.stress.subarray(0, n), 0);
      r.mesh.thinInstancePartialBufferUpdate("instPhase", r.phase.subarray(0, n), 0);
      r.mesh.thinInstancePartialBufferUpdate("instLeanX", r.leanX.subarray(0, n), 0);
      r.mesh.thinInstancePartialBufferUpdate("instLeanZ", r.leanZ.subarray(0, n), 0);
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function smooth(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

/** Deterministic per-slot value in [0,1) for yaw + wind phase variety. */
function hash(i: number): number {
  const h = Math.sin(i * 12.9898) * 43758.5453;
  return h - Math.floor(h);
}
